import { useSyncExternalStore } from 'react'
import {
  type MemberControls,
  PAGE_SURFACE_MAPPINGS,
  pageKeyForView,
  pageKeyForTab,
  isNeverHideableKey,
  type PageKey,
} from '../types/memberControls'
import { subscribeToMyEffectiveControls } from './memberControlsService'

export interface PageVisibilityContextOverride {
  role?: string | null
  isOwner?: boolean
  controls?: MemberControls | null
}

export interface PageVisibilitySnapshot {
  version: number
  isOwner: boolean
  role: string | null
  effectiveControls: MemberControls | null
  workspaceId: string | null
  isHidden: (viewOrTab: string) => boolean
}

export type PageVisibilityHiddenFn = ((viewOrTab: string) => boolean) & PageVisibilitySnapshot

let currentVersion = 0
let currentIsOwner = false
let currentRole: string | null = null
let currentEffectiveControls: MemberControls | null = null
let currentWorkspaceId: string | null = null
let activeEffectiveControlsUnsub: (() => void) | null = null

const listeners = new Set<() => void>()

/**
 * Normalizes an arbitrary view name, tab key, or page key to a canonical PageKey
 * defined in PAGE_SURFACE_MAPPINGS.
 */
export function resolveCanonicalPageKey(viewOrTab: string): PageKey | null {
  if (!viewOrTab) return null
  const trimmed = viewOrTab.trim()
  const lower = trimmed.toLowerCase()

  if (isNeverHideableKey(trimmed) || isNeverHideableKey(lower)) {
    return null
  }

  if (Object.prototype.hasOwnProperty.call(PAGE_SURFACE_MAPPINGS, lower)) {
    return lower as PageKey
  }

  const fromView = pageKeyForView(trimmed)
  if (fromView) return fromView as PageKey

  const fromTab = pageKeyForTab(trimmed) || pageKeyForTab(lower)
  if (fromTab) return fromTab as PageKey

  return null
}

function isPageHiddenInternal(
  viewOrTab: string,
  isOwner: boolean,
  role: string | null,
  controls: MemberControls | null,
): boolean {
  if (!viewOrTab) return false

  const trimmed = viewOrTab.trim()
  const lower = trimmed.toLowerCase()

  if (isNeverHideableKey(trimmed) || isNeverHideableKey(lower)) {
    return false
  }

  if (isOwner === true || role === 'OWNER') {
    return false
  }

  const pageKey = resolveCanonicalPageKey(trimmed)
  if (!pageKey) {
    return false
  }

  if (controls && Array.isArray(controls.tiles)) {
    return controls.tiles.includes(pageKey)
  }

  return false
}

function createSnapshot(
  version: number,
  isOwner: boolean,
  role: string | null,
  effectiveControls: MemberControls | null,
  workspaceId: string | null,
): PageVisibilityHiddenFn {
  const isHidden = (viewOrTab: string): boolean => {
    return isPageHiddenInternal(viewOrTab, isOwner, role, effectiveControls)
  }

  const fn = ((viewOrTab: string): boolean => {
    return isHidden(viewOrTab)
  }) as PageVisibilityHiddenFn

  fn.version = version
  fn.isOwner = isOwner
  fn.role = role
  fn.effectiveControls = effectiveControls
  fn.workspaceId = workspaceId
  fn.isHidden = isHidden

  return fn
}

let currentSnapshot: PageVisibilityHiddenFn = createSnapshot(0, false, null, null, null)
let lastNotifiedIsOwner = false
let lastNotifiedRole: string | null = null
let lastNotifiedWorkspaceId: string | null = null
let lastNotifiedTiles = new Set<string>()

export function getSnapshot(): PageVisibilityHiddenFn {
  return currentSnapshot
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function extractTilesSet(controls: MemberControls | null): Set<string> {
  return new Set(controls && Array.isArray(controls.tiles) ? controls.tiles : [])
}

function areTilesEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false
  for (const item of a) {
    if (!b.has(item)) return false
  }
  return true
}

function notifyStoreChange(): void {
  const nextTiles = extractTilesSet(currentEffectiveControls)
  const isOwnerChanged = currentIsOwner !== lastNotifiedIsOwner
  const roleChanged = currentRole !== lastNotifiedRole
  const wsChanged = currentWorkspaceId !== lastNotifiedWorkspaceId
  const tilesChanged = !areTilesEqual(lastNotifiedTiles, nextTiles)

  if (!isOwnerChanged && !roleChanged && !wsChanged && !tilesChanged) {
    return
  }

  lastNotifiedIsOwner = currentIsOwner
  lastNotifiedRole = currentRole
  lastNotifiedWorkspaceId = currentWorkspaceId
  lastNotifiedTiles = nextTiles

  currentVersion += 1
  currentSnapshot = createSnapshot(
    currentVersion,
    currentIsOwner,
    currentRole,
    currentEffectiveControls,
    currentWorkspaceId,
  )
  for (const listener of Array.from(listeners)) {
    try {
      listener()
    } catch {
      // ignore
    }
  }
}

/**
 * React hook connecting to the page visibility external store.
 * Returns a stable `isHidden(viewOrTab)` function that changes identity
 * whenever the snapshot version changes (effectiveControls, role, or owner change).
 * The returned function also carries the snapshot properties (effectiveControls, isOwner, role, version).
 */
export function usePageVisibility(): PageVisibilityHiddenFn {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/**
 * Checks whether a given view or tab key is hidden for the current user in effectiveControls.
 * Non-React entry point (AI intents, navigation action).
 */
export function isPageHiddenForMe(
  viewOrTab: string,
  contextOverride?: PageVisibilityContextOverride,
): boolean {
  if (contextOverride) {
    const isOwner = contextOverride.isOwner ?? currentIsOwner
    const role = contextOverride.role ?? currentRole
    const controls = contextOverride.controls !== undefined
      ? contextOverride.controls
      : currentEffectiveControls
    return isPageHiddenInternal(viewOrTab, isOwner, role, controls)
  }
  return currentSnapshot.isHidden(viewOrTab)
}

/**
 * Subscribes to the authenticated user's effectiveControls for the active workspace,
 * updating the external store and notifying listeners on every change.
 */
export function subscribePageVisibility(
  workspaceId: string,
  uid?: string,
  isOwner = false,
  role?: string,
): () => void {
  const normalizedRole = role ?? (isOwner ? 'OWNER' : null)
  const roleChanged = currentIsOwner !== isOwner || currentRole !== normalizedRole
  const wsChanged = currentWorkspaceId !== (workspaceId || null)

  currentIsOwner = isOwner
  currentRole = normalizedRole
  currentWorkspaceId = workspaceId || null

  if (activeEffectiveControlsUnsub) {
    activeEffectiveControlsUnsub()
    activeEffectiveControlsUnsub = null
  }

  if (roleChanged || wsChanged) {
    if (wsChanged) {
      currentEffectiveControls = null
    }
    notifyStoreChange()
  }

  if (!workspaceId) {
    return () => {}
  }

  const unsub = subscribeToMyEffectiveControls(
    workspaceId,
    (controls) => {
      currentEffectiveControls = controls
      notifyStoreChange()
    },
    uid,
  )

  activeEffectiveControlsUnsub = unsub

  return () => {
    if (activeEffectiveControlsUnsub === unsub) {
      unsub()
      activeEffectiveControlsUnsub = null
    }
  }
}

/**
 * Sets module-level context for testing or direct state synchronization.
 */
export function setPageVisibilityContext(ctx: {
  isOwner?: boolean
  role?: string | null
  controls?: MemberControls | null
  workspaceId?: string | null
}): void {
  if (ctx.isOwner !== undefined) currentIsOwner = ctx.isOwner
  if (ctx.role !== undefined) currentRole = ctx.role
  if (ctx.controls !== undefined) currentEffectiveControls = ctx.controls
  if (ctx.workspaceId !== undefined) currentWorkspaceId = ctx.workspaceId
  notifyStoreChange()
}

/**
 * Resets module-level state for unit testing isolation.
 */
export function __resetPageVisibilityForTests(): void {
  if (activeEffectiveControlsUnsub) {
    activeEffectiveControlsUnsub()
    activeEffectiveControlsUnsub = null
  }
  currentVersion = 0
  currentIsOwner = false
  currentRole = null
  currentEffectiveControls = null
  currentWorkspaceId = null
  lastNotifiedIsOwner = false
  lastNotifiedRole = null
  lastNotifiedWorkspaceId = null
  lastNotifiedTiles = new Set<string>()
  currentSnapshot = createSnapshot(0, false, null, null, null)
  listeners.clear()
}
